import { PDFDocument, rgb, StandardFonts, degrees } from 'pdf-lib';
import { getPdfPageCount } from '../split/splitPdfEngine';
import {
    colorAdjustPageToNewPage,
    flattenCropToNewPage,
    flattenPerspectiveCropToNewPage,
    normalizeColorAdjust,
    pageHasColorAdjust,
    DEFAULT_COLOR_ADJUST,
    applyColorAdjustments,
} from '../shared/pdfPageEditEngine';
import { isHeicFile, toDisplayableBlob } from '../../image/shared/heicDisplay';

const IMAGE_EXT = /\.(jpe?g|png|webp|avif|gif|heic|heif|hif|bmp)$/i;
const PDF_EXT = /\.pdf$/i;

export const PAGE_SIZE_OPTIONS = ['a4', 'letter', 'a3', 'legal', 'a5'];
export const ORIENTATION_OPTIONS = ['auto', 'portrait', 'landscape'];
export const MARGIN_OPTIONS = ['none', 'small', 'medium', 'large'];
export const IMAGE_FIT_OPTIONS = ['fit', 'fill', 'original'];

export const DEFAULT_CREATE_PDF_SETTINGS = {
    pageSize: 'a4',
    orientation: 'auto',
    margin: 'medium',
    pageNumbers: false,
    imageFit: 'fit',
    imageQuality: 90,
    brightness: 100,
    contrast: 100,
};

const PAGE_SIZES = {
    a4: { width: 595.28, height: 841.89 },
    letter: { width: 612, height: 792 },
    a3: { width: 841.89, height: 1190.55 },
    legal: { width: 612, height: 1008 },
    a5: { width: 419.53, height: 595.28 },
};

const MARGINS = {
    none: 0,
    small: 18,
    medium: 36,
    large: 72,
};

export function isPdfFile(file) {
    return file.type === 'application/pdf' || PDF_EXT.test(file.name);
}

export function isImageFile(file) {
    // Chrome on Windows reports `type === ''` for a .heic, so the name counts too.
    return (file.type || '').startsWith('image/') || IMAGE_EXT.test(file.name) || isHeicFile(file);
}

export function isSupportedCreateFile(file) {
    return isPdfFile(file) || isImageFile(file);
}

function pageLabel(file, pageIndex) {
    const base = file.name.replace(/\.[^.]+$/, '');
    if (isPdfFile(file)) return `${base}-page-${pageIndex + 1}`;
    return file.name;
}

/**
 * Expand uploaded files into individual page entries.
 * @param {File} file
 * @returns {Promise<Array<{ kind: 'image'|'pdf-page', file: File, pageIndex: number, rotation: number, label: string }>>}
 */
export async function expandFileToPages(file) {
    if (isPdfFile(file)) {
        const count = await getPdfPageCount(file);
        return Array.from({ length: count }, (_, pageIndex) => ({
            kind: 'pdf-page',
            file,
            pageIndex,
            rotation: 0,
            removed: false,
            cropRegion: null,
            cropPct: null,
            cropCorners: null,
            cropMode: 'crop',
            ...DEFAULT_COLOR_ADJUST,
            label: pageLabel(file, pageIndex),
        }));
    }

    return [{
        kind: 'image',
        file,
        pageIndex: 0,
        rotation: 0,
        removed: false,
        cropRegion: null,
        cropPct: null,
        cropCorners: null,
        cropMode: 'crop',
        ...DEFAULT_COLOR_ADJUST,
        label: pageLabel(file, 0),
    }];
}

function resolvePageDimensions(pageSize, orientation) {
    const base = PAGE_SIZES[pageSize] || PAGE_SIZES.a4;
    if (orientation === 'landscape') {
        return { width: base.height, height: base.width };
    }
    return { width: base.width, height: base.height };
}

function resolveAutoPageDimensions(pageSize, imageWidth, imageHeight) {
    const base = PAGE_SIZES[pageSize] || PAGE_SIZES.a4;
    const isWide = imageWidth > imageHeight;
    if (isWide) {
        return { width: base.height, height: base.width };
    }
    return { width: base.width, height: base.height };
}

async function loadImageElement(file) {
    const url = URL.createObjectURL(file);
    try {
        return await new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = () => resolve(img);
            img.onerror = reject;
            img.src = url;
        });
    } finally {
        URL.revokeObjectURL(url);
    }
}

async function renderImageToJpegBytes(file, settings, rotation = 0, crop = null, pageColor = null) {
    const img = await loadImageElement(file);
    const natW = img.naturalWidth;
    const natH = img.naturalHeight;

    // Crop region (normalized, relative to the unrotated image) -> source pixels.
    const sx = crop ? Math.round(Math.max(0, crop.left) * natW) : 0;
    const sy = crop ? Math.round(Math.max(0, crop.top) * natH) : 0;
    const sw = crop ? Math.max(1, Math.round(crop.width * natW)) : natW;
    const sh = crop ? Math.max(1, Math.round(crop.height * natH)) : natH;

    const rot = ((rotation % 360) + 360) % 360;
    const swap = rot === 90 || rot === 270;
    const width = swap ? sh : sw;
    const height = swap ? sw : sh;

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, width, height);
    ctx.save();
    ctx.translate(width / 2, height / 2);
    ctx.rotate((rot * Math.PI) / 180);
    ctx.drawImage(img, sx, sy, sw, sh, -sw / 2, -sh / 2, sw, sh);
    ctx.restore();

    const page = normalizeColorAdjust(pageColor);
    const combined = {
        ...page,
        brightness: Math.round((page.brightness * settings.brightness) / 100),
        contrast: Math.round((page.contrast * settings.contrast) / 100),
        saturation: Math.round((page.saturation * (settings.saturation ?? 100)) / 100),
    };

    let output = canvas;
    if (pageHasColorAdjust(combined)) {
        output = applyColorAdjustments(canvas, combined);
    }

    const quality = Math.max(0.5, Math.min(1, settings.imageQuality / 100));
    const blob = await new Promise((resolve, reject) => {
        output.toBlob((result) => (result ? resolve(result) : reject(new Error('render failed'))), 'image/jpeg', quality);
    });
    return { bytes: await blob.arrayBuffer(), width, height };
}

async function normalizeImageBytes(file, settings, rotation = 0, crop = null, pageColor = null) {
    const name = file.name.toLowerCase();
    if (isHeicFile(file)) {
        // Decoded once and shared with the page thumbnails (see heicDisplay).
        const decoded = await toDisplayableBlob(file);
        if (decoded === file) throw new Error('heic decode failed');
        const rendered = await renderImageToJpegBytes(decoded, settings, rotation, crop, pageColor);
        return { ...rendered, kind: 'jpeg' };
    }

    const page = normalizeColorAdjust(pageColor);
    // pdf-lib embeds only JPEG and PNG bytes. Everything that is not a plain JPEG
    // (PNG, WebP, AVIF, GIF, BMP…) is drawn by the browser and re-encoded.
    const type = (file.type || '').toLowerCase();
    const isJpeg = type === 'image/jpeg' || type === 'image/jpg' || (!type && /\.jpe?g$/.test(name));
    const needsRaster = !isJpeg
        || settings.brightness !== 100
        || settings.contrast !== 100
        || pageHasColorAdjust(page)
        || rotation !== 0
        || !!crop;

    if (needsRaster) {
        const rendered = await renderImageToJpegBytes(file, settings, rotation, crop, pageColor);
        return { ...rendered, kind: 'jpeg' };
    }

    const img = await loadImageElement(file);
    return {
        bytes: await file.arrayBuffer(),
        width: img.naturalWidth,
        height: img.naturalHeight,
        kind: 'jpeg',
    };
}

function computeImagePlacement(imageWidth, imageHeight, pageWidth, pageHeight, margin, imageFit) {
    if (imageFit === 'original') {
        const pageW = Math.max(imageWidth + margin * 2, imageWidth);
        const pageH = Math.max(imageHeight + margin * 2, imageHeight);
        return {
            pageWidth: pageW,
            pageHeight: pageH,
            x: (pageW - imageWidth) / 2,
            y: (pageH - imageHeight) / 2,
            drawW: imageWidth,
            drawH: imageHeight,
        };
    }

    const maxW = pageWidth - margin * 2;
    const maxH = pageHeight - margin * 2;

    if (imageFit === 'fill') {
        const scale = Math.max(maxW / imageWidth, maxH / imageHeight);
        const drawW = imageWidth * scale;
        const drawH = imageHeight * scale;
        return {
            pageWidth,
            pageHeight,
            x: (pageWidth - drawW) / 2,
            y: (pageHeight - drawH) / 2,
            drawW,
            drawH,
        };
    }

    const scale = Math.min(maxW / imageWidth, maxH / imageHeight, 1);
    const drawW = imageWidth * scale;
    const drawH = imageHeight * scale;
    return {
        pageWidth,
        pageHeight,
        x: (pageWidth - drawW) / 2,
        y: (pageHeight - drawH) / 2,
        drawW,
        drawH,
    };
}

async function addImageAsPage(pdfDoc, file, settings, rotation = 0, crop = null, pageColor = null) {
    const { bytes, kind, width, height } = await normalizeImageBytes(file, settings, rotation, crop, pageColor);
    const image = kind === 'png'
        ? await pdfDoc.embedPng(bytes)
        : await pdfDoc.embedJpg(bytes);

    const margin = MARGINS[settings.margin] ?? MARGINS.medium;

    let pageDims = settings.orientation === 'auto'
        ? resolveAutoPageDimensions(settings.pageSize, width, height)
        : resolvePageDimensions(settings.pageSize, settings.orientation);

    if (settings.imageFit === 'original' && settings.orientation === 'auto') {
        pageDims = {
            width: width + margin * 2,
            height: height + margin * 2,
        };
    }

    const placement = computeImagePlacement(
        width,
        height,
        pageDims.width,
        pageDims.height,
        margin,
        settings.imageFit,
    );

    const page = pdfDoc.addPage([placement.pageWidth, placement.pageHeight]);
    page.drawImage(image, {
        x: placement.x,
        y: placement.y,
        width: placement.drawW,
        height: placement.drawH,
    });
}

async function loadSourcePdf(file) {
    const bytes = await file.arrayBuffer();
    try {
        return await PDFDocument.load(bytes, { ignoreEncryption: true });
    } catch (err) {
        if (String(err?.message || '').toLowerCase().includes('encrypt')) {
            throw new Error('password');
        }
        throw err;
    }
}

function applyCropRegion(page, region) {
    const { width: pw, height: ph } = page.getSize();
    const cropW = Math.max(1, region.width * pw);
    const cropH = Math.max(1, region.height * ph);
    const x = region.left * pw;
    const y = ph - region.top * ph - cropH;
    page.setCropBox(x, y, cropW, cropH);
    page.setMediaBox(x, y, cropW, cropH);
}

async function appendPdfPage(pdfDoc, file, pageIndex, rotation = 0, crop = null) {
    const source = await loadSourcePdf(file);
    const [page] = await pdfDoc.copyPages(source, [pageIndex]);
    if (rotation) {
        page.setRotation(degrees(rotation));
    }
    if (crop) applyCropRegion(page, crop);
    pdfDoc.addPage(page);
}

async function addPageNumbers(pdfDoc) {
    const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
    const pages = pdfDoc.getPages();
    const total = pages.length;

    pages.forEach((page, index) => {
        const { width } = page.getSize();
        const text = `${index + 1} / ${total}`;
        const size = 10;
        const textWidth = font.widthOfTextAtSize(text, size);
        page.drawText(text, {
            x: (width - textWidth) / 2,
            y: 24,
            size,
            font,
            color: rgb(0.45, 0.45, 0.45),
        });
    });
}

function normalizeSettings(options = {}) {
    return {
        pageSize: PAGE_SIZE_OPTIONS.includes(options.pageSize) ? options.pageSize : DEFAULT_CREATE_PDF_SETTINGS.pageSize,
        orientation: ORIENTATION_OPTIONS.includes(options.orientation) ? options.orientation : DEFAULT_CREATE_PDF_SETTINGS.orientation,
        margin: MARGIN_OPTIONS.includes(options.margin) ? options.margin : DEFAULT_CREATE_PDF_SETTINGS.margin,
        pageNumbers: Boolean(options.pageNumbers),
        imageFit: IMAGE_FIT_OPTIONS.includes(options.imageFit) ? options.imageFit : DEFAULT_CREATE_PDF_SETTINGS.imageFit,
        imageQuality: Number.isFinite(options.imageQuality) ? options.imageQuality : DEFAULT_CREATE_PDF_SETTINGS.imageQuality,
        brightness: Number.isFinite(options.brightness) ? options.brightness : DEFAULT_CREATE_PDF_SETTINGS.brightness,
        contrast: Number.isFinite(options.contrast) ? options.contrast : DEFAULT_CREATE_PDF_SETTINGS.contrast,
    };
}

/**
 * Build one PDF from ordered page entries.
 * @param {Array<{ kind: 'image'|'pdf-page', file: File, pageIndex: number, rotation?: number }>} pages
 * @param {object} [options]
 * @param {(pageIndex: number, pageTotal: number) => void} [onProgress]
 */
export async function createPdfFromPages(pages, options = {}, onProgress) {
    const settings = normalizeSettings(options);
    const pdfDoc = await PDFDocument.create();

    // Removed pages are kept in the editor state but excluded from the output.
    const active = pages.filter((entry) => !entry.removed);
    if (!active.length) throw new Error('empty');

    for (let i = 0; i < active.length; i++) {
        onProgress?.(i + 1, active.length);
        const entry = active[i];
        const rotation = entry.rotation || 0;
        const crop = entry.cropRegion || null;
        const flatten = entry.cropMode === 'flatten';
        const color = normalizeColorAdjust(entry);
        const needsColor = pageHasColorAdjust(entry);

        if (entry.kind === 'pdf-page') {
            if (flatten && (entry.cropCorners?.length === 4 || crop)) {
                const source = await loadSourcePdf(entry.file);
                const srcPage = source.getPages()[entry.pageIndex];
                const size = srcPage?.getSize?.() || { width: 595, height: 842 };
                if (entry.cropCorners?.length === 4) {
                    await flattenPerspectiveCropToNewPage(
                        pdfDoc,
                        entry.file,
                        entry.pageIndex,
                        entry.cropCorners,
                        rotation,
                        size,
                        color,
                    );
                } else {
                    await flattenCropToNewPage(
                        pdfDoc, entry.file, entry.pageIndex, crop, rotation, size, color,
                    );
                }
            } else if (needsColor) {
                const source = await loadSourcePdf(entry.file);
                const srcPage = source.getPages()[entry.pageIndex];
                const size = srcPage?.getSize?.() || { width: 595, height: 842 };
                await colorAdjustPageToNewPage(
                    pdfDoc, entry.file, entry.pageIndex, crop, rotation, size, color,
                );
            } else {
                await appendPdfPage(pdfDoc, entry.file, entry.pageIndex, rotation, crop);
            }
        } else if (entry.kind === 'image') {
            await addImageAsPage(pdfDoc, entry.file, settings, rotation, crop, color);
        } else {
            throw new Error('unsupported');
        }
    }

    if (pdfDoc.getPageCount() === 0) throw new Error('empty');

    if (settings.pageNumbers) {
        await addPageNumbers(pdfDoc);
    }

    const output = await pdfDoc.save();
    return new Blob([output], { type: 'application/pdf' });
}

/** @deprecated Use createPdfFromPages */
export async function createPdfFromFiles(files, options = {}, onProgress) {
    const pages = [];
    for (const file of files) {
        pages.push(...await expandFileToPages(file));
    }
    return createPdfFromPages(pages, options, onProgress);
}
